import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { AuthContext } from "../contexts/auth";
import {
  fetchDocsGroupedV2,
  fetchDocsByEmployeeV2,
  updateDocVisibility,
  deleteDoc,
} from "../services/api";

import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import moment from "moment";
import { Link } from "wouter";
import Swal from "sweetalert2";

import ListBoxQueryMultiSelectEmployeesActiveContract from "./list_box_query_employee_active_contract";
import Fetching from "./fetching";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ComingSoon from "../routes/coming_soon";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, IconButton, Paper, Typography, Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CustomTooltipComponent from "./custom_tooltip_component";
import { MagnifyingGlassIcon, XCircleIcon, CheckCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import FilePreviewModal from "./preview_modal";
import EditDocumentModal from "./edit_document_modal";
import { formatEmails, buildDefaultEmailCsv } from "../services/utils_email";
import FirmeCompany from "../routes/firme_company";
import PrettyPagination from "./pretty_pagination";
import FirmeCreditsBar from "./firme_credits_bar";
import ModalBuyFirmeCredits from "./modal_buy_firme_credits";

const getEffectiveNotification = (doc, defaults) => {
  const timing =
    doc?.notification_timing != null
      ? doc.notification_timing
      : defaults?.default_days != null
        ? defaults.default_days
        : null;


  const defaultEmailCsv = buildDefaultEmailCsv({
    send_company: defaults?.send_company,
    company_email: defaults?.company_email,
    send_handlers: defaults?.send_handlers,
    handlers_emails: defaults?.handlers_emails,
    others_emails: defaults?.others_emails,
  });

  const email =
    doc?.notification_email != null && String(doc.notification_email).trim() !== ""
      ? String(doc.notification_email)
      : defaultEmailCsv;

  return { timing, email };
};

export default function DocsTable({ token }) {
  const { profile } = useContext(AuthContext);
  const [grouped, setGrouped] = useState([]);
  const [fetching, setFetching] = useState(true);

  // dettaglio per employee
  const [detailsByEmployeeId, setDetailsByEmployeeId] = useState({});

  const [selectedTab, setSelectedTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") === "firmati" ? "firmati" : "caricati";
  });
  // selezione ListBox (solo UI)
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  // filtro server-side
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [openEmployee, setOpenEmployee] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  // paginazione server-side
  const [page, setPage] = useState(1);
  // search testuale server-side
  const [q, setQ] = useState("");

  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [creditsRefreshKey, setCreditsRefreshKey] = useState(0);
  const [feaCredits, setFeaCredits] = useState(null);

  const handleBuyCredits = () => {
    setIsBuyCreditsModalOpen(true);
  };

  // response pagination
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page_size: null,
  });

  // calcolo pagine SOLO da dati backend
  const totalPages = useMemo(() => {
    const effectivePageSize = pagination.page_size || 50;

    return Math.max(
      1,
      Math.ceil((pagination.count || 0) / effectivePageSize)
    );
  }, [pagination.count, pagination.page_size]);

  // ----------------- LOAD SUMMARY (server paginated) -----------------
  useEffect(() => {
  if (!token) return;

  setFetching(true);

  // Qui passiamo FILTRI AL BACKEND
  fetchDocsGroupedV2(token, "documents", {
    page,
    q: q || undefined,
    employee_id: selectedEmployeeId || undefined,
  })
    .then((res) => {
      setGrouped(Array.isArray(res?.results) ? res.results : []);
      setPagination({
        count: res?.count ?? 0,
        next: res?.next ?? null,
        previous: res?.previous ?? null,
        page_size: res?.page_size ?? 50,
      });
    })
    .catch(console.error)
    .finally(() => setFetching(false));
  }, [token, page, q, selectedEmployeeId]);

  // ----------------- PAGINATION UI -----------------
  const handlePageChange = (value) => {
    setPage(value);
    setOpenEmployee(null);
  };

  // ----------------- SELEZIONE EMPLOYEE (ListBox => filtro server) -----------------
  const handleEmployeeSelection = (employee) => {
    if (!employee?.id) return;

    setSelectedEmployees([employee]); // UI
    setSelectedEmployeeId(employee.id); // filtro server
    setPage(1);
    setOpenEmployee(null);
  };

  const resetEmployeeSelection = () => {
    setSelectedEmployees([]);
    setSelectedEmployeeId(null);
    setPage(1);
    setOpenEmployee(null);
  };

  // ----------------- LAZY LOAD DOCS PER EMPLOYEE -----------------
  const ensureEmployeeDetailLoaded = useCallback(
    async (employee) => {
      const employeeId = employee?.id;
      if (!employeeId) return;

      const current = detailsByEmployeeId[employeeId];
      if (current?.loading || current?.loaded) return;

      setDetailsByEmployeeId((prev) => ({
        ...prev,
        [employeeId]: { loading: true, loaded: false, documents: [] },
      }));

      try {
        const data = await fetchDocsByEmployeeV2(token, employeeId, "documents");
        const docs = Array.isArray(data?.documents) ? data.documents : [];
        const defaults = data?.defaults || null; 

        const docsWithEmployee = docs.map((d) => ({ ...d, employee: data.employee }));

        setDetailsByEmployeeId((prev) => ({
          ...prev,
          [employeeId]: {
            loading: false,
            loaded: true,
            documents: docsWithEmployee,
            defaults,
          },
        }));
      } catch (err) {
        console.error(err);
        setDetailsByEmployeeId((prev) => ({
          ...prev,
          [employeeId]: { loading: false, loaded: true, documents: [], error: true },
        }));
      }
    },
    [token, detailsByEmployeeId]
  );

  // ----------------- VISIBILITY -----------------
  const handleVisibilityChange = useCallback(
    (docId, currentVisibility) => {
      const message = currentVisibility
        ? 'Vuoi rendere il documento non visibile al dipendente?'
        : 'Vuoi rendere il documento visibile al dipendente?';

      Swal.fire({
        title: message,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sì, confermo',
        cancelButtonText: 'No, annulla',
      }).then((result) => {
        if (!result.isConfirmed) return;

        updateDocVisibility(token, docId, !currentVisibility)
          .then(() => {
            setDetailsByEmployeeId((prev) => {
              const next = { ...prev };
              for (const key of Object.keys(next)) {
                const block = next[key];
                if (!block?.documents?.length) continue;
                next[key] = {
                  ...block,
                  documents: block.documents.map((d) =>
                    d.id === docId
                      ? { ...d, visible_to_employee: !currentVisibility }
                      : d
                  ),
                };
              }
              return next;
            });
            Swal.fire('Salvato!', '', 'success');
          })
          .catch((err) => {
            console.error(err);
            Swal.fire('Errore!', 'Si è verificato un errore durante l\'aggiornamento.', 'error');
          });
      });
    },
    [token]
  );

  // ----------------- DELETE -----------------
  const handleDeleteDoc = useCallback(
    (docId) => {
      Swal.fire({
        title: 'Sei sicuro di voler cancellare questo file?',
        text: 'Una volta cancellato non sarai in grado di recuperarlo!',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#a3a3a3',
        confirmButtonText: 'Sì, elimina',
        cancelButtonText: 'Annulla',
      }).then((result) => {
        if (!result.isConfirmed) return;

        deleteDoc(token, docId)
          .then(() => {
            Swal.fire('Cancellato!', 'Il file è stato eliminato.', 'success');

            setDetailsByEmployeeId((prev) => {
              const next = { ...prev };
              for (const key of Object.keys(next)) {
                const block = next[key];
                if (!block?.documents?.length) continue;
                next[key] = {
                  ...block,
                  documents: block.documents.filter((d) => d.id !== docId),
                };
              }
              return next;
            });

            // ricarica lista corrente (stessi filtri)
            // (invece di setGrouped) => basta triggerare effect
            // qui puoi fare setPage(page) ma non cambia.
            // quindi richiama esplicitamente:
            setFetching(true);
            fetchDocsGroupedV2(token, "documents", {
              page,
              q: q || undefined,
              employee_id: selectedEmployeeId || undefined,
            })
              .then((res) => {
                setGrouped(Array.isArray(res?.results) ? res.results : []);
                setPagination({
                  count: res?.count ?? 0,
                  next: res?.next ?? null,
                  previous: res?.previous ?? null,
                  page_size: res?.page_size ?? 50,
                });
              })
              .catch(console.error)
              .finally(() => setFetching(false));
          })
          .catch((err) => {
            Swal.fire({
              icon: 'error',
              title: 'Oops…',
              text: err?.message || 'Errore inatteso durante l’eliminazione.',
            });
          });
      });
    },
    [token, page, q, selectedEmployeeId]
  );

  // ----------------- UPDATE FROM MODAL -----------------
  const handleDocUpdated = useCallback((updated) => {
    if (!updated?.id) return;

    setDetailsByEmployeeId((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        const block = next[key];
        if (!block?.documents?.length) continue;

        const idx = block.documents.findIndex((d) => d.id === updated.id);
        if (idx === -1) continue;

        const newDocs = [...block.documents];
        newDocs[idx] = { ...newDocs[idx], ...updated, employee: newDocs[idx].employee };
        next[key] = { ...block, documents: newDocs };
      }
      return next;
    });

    Swal.fire({
      icon: 'success',
      title: 'Documento aggiornato',
      timer: 1500,
      showConfirmButton: false,
    });
  }, []);

  return (
    <>
      {/* TABS DOCUMENTI E FILTRO DIPENDENTE */}
      <div className="overflow-visible rounded-xl border border-gray-300 bg-white">
        {/* INTESTAZIONE E SELEZIONE TIPO DOCUMENTO */}
        <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h3 className="text-base font-semibold uppercase text-indigo-800">
              Gestione documenti dipendenti
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Scegli la tipologia di documenti da visualizzare
            </p>
          </div>
          <div className="grid w-full grid-cols-1 overflow-hidden rounded-lg shadow-sm sm:grid-cols-2 lg:w-auto lg:min-w-[440px]">
            <button
              type="button"
              onClick={() => setSelectedTab("caricati")}
              className={`relative flex min-w-0 items-center justify-center gap-2 border px-4 py-3 text-xs font-semibold transition sm:rounded-l-lg sm:text-sm ${
                selectedTab === "caricati"
                  ? "z-10 border-indigo-300 bg-indigo-100 text-indigo-800"
                  : "border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100"
              }`}
            >
              <UploadFileOutlinedIcon sx={{ fontSize: 20 }} className="shrink-0" />
              <span className="truncate">Caricati</span>
            </button>
            <button
              type="button"
              onClick={() => setSelectedTab("firmati")}
              className={`relative flex min-w-0 items-center justify-center gap-2 border px-4 py-3 text-xs font-semibold transition sm:rounded-r-lg sm:text-sm ${
                selectedTab === "firmati"
                  ? "z-10 border-indigo-300 bg-indigo-100 text-indigo-800"
                  : "border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100"
              }`}
            >
              <DrawOutlinedIcon sx={{ fontSize: 20 }} className="shrink-0" />
              <span className="truncate">Con Firma</span>
            </button>
          </div>
        </div>
        {/* SEPARATORE */}
        <div className="border-t border-gray-200" />
        {/* RICERCA DIPENDENTE */}
        <div className="px-4 py-3 md:px-6">
          <label className="mb-1 block text-[11px] uppercase tracking-wide text-gray-600">
            Cerca dipendente
          </label>
          <div className="w-full ">
            <ListBoxQueryMultiSelectEmployeesActiveContract
              token={token}
              selectedEmployees={selectedEmployees}
              onChange={(selected) => {
                const first = Array.isArray(selected) ? selected[0] : selected;
                handleEmployeeSelection(first);
              }}
              employeeRolePairs={selectedEmployees.map((e) => ({ employeeId: e.id }))}
              placeholder="Digita per cercare un dipendente..."
              filterBy="all"
              pageSize={10}
              viewEmail={true}                 
            />
          </div>
          {selectedEmployees.length > 0 && (
            <div className="mt-2 flex justify-start">
              <button
                type="button"
                onClick={resetEmployeeSelection}
                className="text-xs font-semibold text-indigo-600 transition hover:text-indigo-800 hover:underline"
              >
                Reset selezione
              </button>
            </div>
          )}
        </div>
        {/* AZIONI */}
        <div className="rounded-b-xl bg-gray-50 px-4 py-3 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

            {/* PULSANTE CARICAMENTO - SINISTRA */}
            <Link
              to={`/${selectedTab === "caricati" ? "upload_documento" : "upload_documento_firmato"}`}
              className="inline-flex w-fit shrink-0 items-center justify-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500"
            >
              {selectedTab === "caricati"
                ? <UploadFileOutlinedIcon sx={{ fontSize: 18 }} />
                : <DrawOutlinedIcon sx={{ fontSize: 18 }} />
              }
              {selectedTab === "caricati"
                ? "Carica nuovo documento"
                : "Carica nuovo documento da firmare"
              }
            </Link>

            {/* CREDITI FEA E PULSANTE - DESTRA */}
            {selectedTab === "firmati" && (
              <div className="flex min-w-0 items-center justify-start lg:justify-end">
                <FirmeCreditsBar
                  onBuyCredits={handleBuyCredits}
                  onBalanceChange={setFeaCredits}
                  refreshKey={creditsRefreshKey}
                />
              </div>
            )}

          </div>
        </div>
      </div>

      {/* CONTENUTO DOCUMENTI */}
      <div className="flow-root">
        {selectedTab === "firmati" ? (
          <Box sx={{ mx: 0, my: 4 }}>
            <FirmeCompany
              token={token}
              selectedEmployee={selectedEmployees[0] || null}
            />
          </Box>
        ) : (
          <div className="pt-8">
            <TableContainer component={Paper} sx={{ maxHeight: "100%" }} className="!border !border-gray-300 !rounded-lg shadow-md">
              {fetching ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Fetching />
                </Box>
              ) : (
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow className="h-12 font-semibold uppercase">
                      <TableCell className="!bg-indigo-50/50" />
                      <TableCell className="!text-indigo-800 !font-semibold !bg-indigo-50/50">Dipendente</TableCell>
                      <TableCell className="!text-indigo-800 !font-semibold !bg-indigo-50/50" align="left">
                        Documenti
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {grouped.length ? (
                      grouped.map((row) => (
                        <EmployeeRow
                          key={row.employee?.id}
                          employee={row.employee}
                          docsCount={row.docs_count}
                          docs={detailsByEmployeeId[row.employee.id]?.documents || []}
                          defaults={detailsByEmployeeId[row.employee.id]?.defaults || null}
                          loadingDocs={!!detailsByEmployeeId[row.employee.id]?.loading}
                          onOpenEmployee={() => ensureEmployeeDetailLoaded(row.employee)}
                          onToggleVisible={handleVisibilityChange}
                          openEmployee={openEmployee}
                          setOpenEmployee={setOpenEmployee}
                          setPreviewDoc={setPreviewDoc}
                          onDelete={handleDeleteDoc}
                          onUpdated={handleDocUpdated}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }} className="italic">
                          Nessun documento disponibile
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </div>
        )}

        {/* PAGINAZIONE DOCUMENTI */}
        {selectedTab === "caricati" && (
          <PrettyPagination
            page={page}
            totalPages={totalPages}
            totalCount={pagination.count}
            onChange={handlePageChange}
            labelSingolare="dipendente"
            labelPlurale="dipendenti"
            className="pb-3 pt-6 border-x border-b border-gray-300"
          />
        )}
      </div>

      <FilePreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    
      {/* MODALE ACQUISTO FIRME FEA */}
      <ModalBuyFirmeCredits
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
        availableCredits={feaCredits}
        useFreeCredits={profile?.customer_type === 'company'}
        token={token}
        storageScope={JSON.stringify([profile?.the_real_user_email, profile?.company?.id, profile?.company?.email])}
        enabled={profile?.customer_type !== 'employee'}
        onBalance={value => { setFeaCredits(value); setCreditsError(false); }}
      />
    </>
  );
}

/* ---------------- EMPLOYEE ROW ---------------- */
function EmployeeRow({
  employee,
  docs,
  defaults,
  docsCount,
  loadingDocs,
  onOpenEmployee,
  onToggleVisible,
  openEmployee,
  setOpenEmployee,
  setPreviewDoc,
  onDelete,
  onUpdated,
}) {
  const isOpen = openEmployee === employee.cf;
  const [openDocId, setOpenDocId] = useState(null);

  const toggle = () => {
    if (isOpen) {
      setOpenDocId(null);
      setOpenEmployee(null);
    } else {
      setOpenEmployee(employee.cf);
      onOpenEmployee && onOpenEmployee();
    }
  };

  useEffect(() => {
    if (!isOpen && openDocId !== null) setOpenDocId(null);
  }, [isOpen, openDocId]);

  const fullName =
    employee.last_name && employee.first_name
      ? `${employee.last_name} ${employee.first_name}`
      : employee.cf?.toUpperCase();

  const stop = (e) => e.stopPropagation();

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={toggle}>
        <TableCell width={40} onClick={stop}>
          <IconButton size="small" onClick={toggle}>
            {isOpen ? (
              <CustomTooltipComponent title="Chiudi dettagli">
                <KeyboardArrowUpIcon />
              </CustomTooltipComponent>
            ) : (
              <CustomTooltipComponent title="Apri dettagli">
                <KeyboardArrowDownIcon />
              </CustomTooltipComponent>
            )}
          </IconButton>
        </TableCell>

        <TableCell className="text-gray-500 h-14">
          <CustomTooltipComponent title="Vai all'anagrafica">
            <Link to={`/dipendenti/${employee.cf}`} className="hover:underline hover:text-indigo-700" onClick={stop}>
              {fullName}
            </Link>
          </CustomTooltipComponent>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2" color="text.secondary">
            {docsCount} document{docsCount !== 1 ? "i" : "o"}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow className="bg-indigo-50">
        <TableCell style={{ padding: 0 }} colSpan={3}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Typography variant="p" gutterBottom component="div" className="font-semibold text-indigo-800 uppercase">
                Documenti di {employee.last_name && employee.first_name ? fullName : employee.cf}
              </Typography>

              {loadingDocs ? (
                <Box sx={{ py: 2 }}>
                  <Fetching />
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="!text-sm"></TableCell>
                      <TableCell className="!text-sm font-semibold">Nome documento</TableCell>
                      <TableCell className="!text-sm font-semibold">Data caricamento</TableCell>
                      <TableCell className="!text-sm font-semibold">Data scadenza</TableCell>
                      <TableCell className="!text-sm font-semibold">Aperto</TableCell>
                      <TableCell className="!text-sm font-semibold" align="center">
                        Visibile al dipendente
                      </TableCell>
                      <TableCell className="!text-sm font-semibold" align="right"></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody className="bg-gray-50">
                    {(docs || []).map((d) => (
                      <DocumentRow
                        key={d.id}
                        doc={d}
                        defaults={defaults}
                        onToggleVisible={onToggleVisible}
                        openDocId={openDocId}
                        setOpenDocId={setOpenDocId}
                        setPreviewDoc={setPreviewDoc}
                        onDelete={onDelete}
                        onUpdated={onUpdated}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* ---------------- DOCUMENT ROW ---------------- */
function DocumentRow({ doc, defaults, onToggleVisible, openDocId, setOpenDocId, setPreviewDoc, onDelete, onUpdated }) {
  const isOpen = openDocId === doc.id;
  const toggle = () => setOpenDocId(isOpen ? null : doc.id);

  const stop = (e) => e.stopPropagation();
  const [openEdit, setOpenEdit] = useState(false);

  const { timing, email } = getEffectiveNotification(doc, defaults);

  const canEditDocument = doc?.employee?.can_edit_document;

  const editTooltipTitle = canEditDocument
    ? "Modifica documento"
    : "La modifica e la notifica delle scadenze sono possibili solo per i dipendenti attivi";

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={toggle}>
        <TableCell width={40} onClick={stop}>
          <IconButton size="small" onClick={toggle}>
            {isOpen ? (
              <CustomTooltipComponent title="Chiudi dettagli">
                <KeyboardArrowUpIcon />
              </CustomTooltipComponent>
            ) : (
              <CustomTooltipComponent title="Apri dettagli">
                <KeyboardArrowDownIcon />
              </CustomTooltipComponent>
            )}
          </IconButton>
        </TableCell>

        <TableCell>{doc.name}</TableCell>
        <TableCell>{moment(doc.created_at).format("DD/MM/YYYY HH:mm")}</TableCell>
        <TableCell>{doc.due_date ? moment(doc.due_date).format("DD/MM/YYYY") : ""}</TableCell>

        <TableCell>
          {doc?.status === "read" ? (
            <>
              <CheckCircleIcon className="h-4 w-4 text-green-500 inline-block mr-1" />
              {doc.read_at ? moment(doc.read_at).format("DD/MM/YYYY HH:mm") : ""}
            </>
          ) : (
            <>
              <XCircleIcon className="h-4 w-4 text-red-500 inline-block mr-1" />
              <span className="text-red-500">Non letto</span>
            </>
          )}
        </TableCell>

        <TableCell align="right" sx={{ p: 0, verticalAlign: "middle" }}>
          <CustomTooltipComponent title="Modifica visibilità">
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }} className="pr-6">
              {doc.visible_to_employee ? (
                <EyeIcon className="w-5 h-5 text-green-500 cursor-pointer" onClick={() => onToggleVisible(doc.id, true)} />
              ) : (
                <EyeSlashIcon className="w-5 h-5 text-red-500 cursor-pointer" onClick={() => onToggleVisible(doc.id, false)} />
              )}
            </Box>
          </CustomTooltipComponent>
        </TableCell>

        <TableCell align="right" sx={{ p: 0, verticalAlign: "middle" }}>
          <dd className="col-span-2 flex gap-3">
            <CustomTooltipComponent title="Apri anteprima">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewDoc({ url: doc.download_link, name: doc.name, description: doc.description });
                }}
                className="flex items-center gap-1 text-indigo-600 hover:underline"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
            </CustomTooltipComponent>

            <CustomTooltipComponent title={editTooltipTitle}>
              <span className="inline-flex items-center">
                <button
                  disabled={!canEditDocument}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canEditDocument) return;
                    setOpenEdit(true);
                  }}
                  className={`p-2 font-bold ${
                    canEditDocument
                      ? "hover:text-indigo-700 text-gray-700"
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <EditOutlinedIcon className="h-5 w-5" fontSize="small" />
                </button>
              </span>
            </CustomTooltipComponent>

            <CustomTooltipComponent title="Elimina">
              <button onClick={() => onDelete(doc.id)} className="p-2 hover:text-red-700 text-gray-700 font-bold">
                <TrashIcon className="h-5 w-5" />
              </button>
            </CustomTooltipComponent>
          </dd>
        </TableCell>
      </TableRow>

      {/* ---------- RIGA “DETAIL”  ---------- */}
      <TableRow>
        <TableCell style={{ padding:0 }} colSpan={7}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <div className="flex flex-wrap gap-4 px-4 py-1 bg-gray-50">
              {/* ---------- colonna UNICA: metadati + azioni ---------- */}
              <div className="w-full bg-gray-50">
                <dl className="divide-y divide-gray-100 text-sm">
                  {/* Nome */}
                  <div className="grid grid-cols-3 gap-2 px-4 py-2">
                    <dt className="font-semibold text-gray-600">Nome documento</dt>
                    <dd className="col-span-2 text-gray-700">{doc.name}</dd>
                  </div>

                  {/* Descrizione */}
                  {doc.description && (
                    <div className="grid grid-cols-3 gap-2 px-4 py-2">
                      <dt className="font-semibold text-gray-600">Descrizione</dt>
                      <dd className="col-span-2 text-gray-700">{doc.description}</dd>
                    </div>
                  )}

                  {/* Caricamento */}
                  <div className="grid grid-cols-3 gap-2 px-4 py-2">
                    <dt className="font-semibold text-gray-600">Caricamento</dt>
                    <dd className="col-span-2 text-gray-700">
                      {moment(doc.created_at).format('DD/MM/YYYY HH:mm')}
                    </dd>
                  </div>

                  {/* Scadenza */}
                  <div className="grid grid-cols-3 gap-2 px-4 py-2">
                    <dt className="font-semibold text-gray-600">Scadenza</dt>
                    <dd className="col-span-2 text-gray-700">
                      {doc?.due_date ? moment(doc.due_date).format('DD/MM/YYYY') : 'Nessuna scadenza'}
                    </dd>
                  </div>

                  <div className="grid grid-cols-3 gap-2 px-4 py-2">
                    <dt className="font-semibold text-gray-600">Aperto</dt>
                    <dd className="col-span-2 text-gray-700">
                      {doc?.status === 'read' ? (
                        <>
                          <CheckCircleIcon className="h-4 w-4 text-green-500 inline-block mr-1" />
                          {doc.read_at ? moment(doc.read_at).format("DD/MM/YYYY HH:mm") : ''}
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-4 w-4 text-red-500 inline-block mr-1" />
                          <span className="text-red-500">Non letto</span>
                        </>
                      )}
                    </dd>
                  </div>
                  {canEditDocument && (
                    <>
                      {/* Promemoria scadenza */}
                      {doc?.due_date && (
                        <div className="grid grid-cols-3 gap-2 px-4 py-2">
                          <dt className="font-semibold text-gray-600">Promemoria scadenza</dt>
                          <dd className="col-span-2 text-gray-700">
                            {timing != null ? `${timing} giorni` : "Nessun promemoria"}
                          </dd>
                        </div>
                      )}

                      {/* Email promemoria */}
                      {doc?.due_date && timing != null && (
                        <div className="grid grid-cols-3 gap-2 px-4 py-2">
                          <dt className="font-semibold text-gray-600">Email di notifica scadenza</dt>
                          <dd className="col-span-2 text-gray-700 whitespace-normal break-words">
                            {formatEmails(email) || "—"}
                          </dd>
                        </div>
                      )}
                    </>
                  )}


                  {/* Visualizza file */}
                  <div className="grid grid-cols-3 gap-2 px-4 py-2 items-center">
                    <dt className="font-semibold text-gray-600">Visualizza file</dt>
                    <dd className="col-span-2 flex items-center gap-2">
                      <button
                        onClick={() =>
                          setPreviewDoc({
                            url: doc.download_link,
                            name: doc.name,
                            description: doc.description,
                          })
                        }
                        className="flex items-center gap-1 text-indigo-600 hover:underline"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Apri anteprima</span>
                      </button>
                    </dd>
                  </div>

                  {/* Azioni */}
                  <div className="grid grid-cols-3 gap-2 px-4 py-2 items-center">
                    <dt className="font-semibold text-gray-600">Azioni</dt>
                    <dd className="col-span-2 flex gap-3">
                      {/* Modifica */}
                      <CustomTooltipComponent title={editTooltipTitle}>
                        <span className="inline-flex items-center">
                          <button
                            disabled={!canEditDocument}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canEditDocument) return;
                              setOpenEdit(true);
                            }}
                            className={`p-2 font-bold ${
                              canEditDocument
                                ? "hover:text-indigo-700 text-gray-700"
                                : "text-gray-400 cursor-not-allowed"
                            }`}
                          >
                            <EditOutlinedIcon className="h-5 w-5" fontSize="small" />
                          </button>
                        </span>
                      </CustomTooltipComponent>

                      {/* elimina */}
                      <CustomTooltipComponent title="Elimina">
                        <button
                          onClick={() => onDelete(doc.id)}
                          className="p-2 hover:text-red-700 text-gray-700 font-bold"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </CustomTooltipComponent>

                      {/* toggle visibilità */}
                      <CustomTooltipComponent title='Modifica visibilità'>
                        <button
                          onClick={() => onToggleVisible(doc.id, doc.visible_to_employee)}
                          className={`p-2 rounded ${
                            doc.visible_to_employee
                              ? 'hover:text-red-700'
                              : 'hover:text-green-700'
                          } text-white`}
                        >
                          {doc.visible_to_employee ? (
                            <EyeIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <EyeSlashIcon className="h-5 w-5 text-red-600" />
                          )}
                        </button>
                      </CustomTooltipComponent>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>

      <EditDocumentModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        doc={doc}
        onSaved={(updated) => {
          setOpenEdit(false);
          onUpdated && onUpdated(updated);
        }}
        mode="edit"
        noButtonPreview="noButtonPreview"
      />
    </>
  );
}